"use client";

/**
 * TraderLabelEditor — inline editor for the user's PRIVATE label (tag) on a
 * trader. Backed by user_trader_labels (mig 277): keyed by trader address,
 * survives unfollow/refollow, never visible to other users.
 *
 * The editor opens as a PORTALED popover (rendered into document.body with
 * fixed positioning clamped to the viewport). Two layout traps drove this:
 *  1. list rows constrain the name cell to a fixed width (w-40 in the
 *     Traders tab) — an inline input gets clipped;
 *  2. profile cards have overflow-hidden ancestors — even an absolutely
 *     positioned child gets cut at the card edge.
 * A body portal escapes both, so the same component works everywhere.
 *
 * Modes:
 *  - "name" (default): renders the display name (label > username > short
 *    address) as a Link/span with a pencil next to it.
 *  - "chip": renders a small tag chip next to an existing name (trader
 *    profile header, where the real handle must stay visible). No label →
 *    a subtle "+ tag" affordance.
 *
 * Self-contained: owns its editing state and the PUT call (including the
 * explicit delete button when a label exists). Parent receives the saved
 * value via onSaved to update its own copy of the data.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { FiEdit2, FiCheck, FiX, FiTag, FiTrash2 } from "react-icons/fi";
import { authFetch } from "@/lib/authFetch";
import { useT } from "@/hooks/useT";
import { createLogger } from "@/lib/logger";

const logger = createLogger("TraderLabelEditor");

const POPOVER_WIDTH = 272;

export function traderDisplayName(
  username?: string | null,
  address?: string | null,
  customLabel?: string | null
): string {
  if (customLabel && customLabel.trim()) return customLabel.trim();
  if (username && !username.startsWith("0x")) return username;
  const addr = (address || username || "").split("-")[0] || "";
  if (/^0x[0-9a-fA-F]{8,}/.test(addr)) return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
  return username || addr || "—";
}

interface Props {
  address: string;
  username?: string | null;
  customLabel?: string | null;
  onSaved: (label: string | null) => void;
  mode?: "name" | "chip";
  /** "name" mode: when set, the display name links here. */
  href?: string;
  /** "name" mode: className for the name Link/span. */
  nameClassName?: string;
}

export default function TraderLabelEditor({
  address,
  username,
  customLabel,
  onSaved,
  mode = "name",
  href,
  nameClassName = "font-satoshi font-semibold text-sm text-gray-900 dark:text-white truncate min-w-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors",
}: Props) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // Position the portaled popover under the anchor, clamped to the viewport
  // so it never renders off-screen or clipped. Flips above the anchor when
  // there's no room below. Re-measured on scroll/resize.
  useEffect(() => {
    if (!editing) return;
    const place = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const left = Math.min(Math.max(8, r.left), window.innerWidth - POPOVER_WIDTH - 8);
      const estimatedHeight = 96; // input row + hint + padding
      const top = r.bottom + 6 + estimatedHeight > window.innerHeight
        ? Math.max(8, r.top - estimatedHeight - 6)
        : r.bottom + 6;
      setPos({ top, left });
    };
    place();
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || popRef.current?.contains(target)) return;
      setEditing(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", onDown);
    };
  }, [editing]);

  const persist = async (value: string | null) => {
    setSaving(true);
    try {
      const res = await authFetch("/api/copytrading/trader-label", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address, label: value }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onSaved(value);
      setEditing(false);
    } catch (error) {
      logger.error("Failed to save trader label", { error: (error as Error)?.message });
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    const value = draft.trim().slice(0, 40);
    persist(value || null);
  };

  const startEditing = () => {
    setDraft(customLabel || "");
    setEditing(true);
  };

  const popover =
    editing && pos && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={popRef}
            style={{ position: "fixed", top: pos.top, left: pos.left, width: POPOVER_WIDTH }}
            className="z-[9999] p-2.5 rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] shadow-xl"
          >
            <form
              className="flex items-center gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                save();
              }}
            >
              <input
                autoFocus
                value={draft}
                maxLength={40}
                placeholder={t("ct.label.placeholder")}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setEditing(false);
                }}
                className="flex-1 min-w-0 px-2 py-1.5 text-sm font-satoshi rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-gray-400 dark:focus:border-[#3A3A3A]"
              />
              <button
                type="submit"
                disabled={saving}
                className="p-1.5 rounded-[5px] text-emerald-500 hover:bg-emerald-500/10 transition-colors disabled:opacity-50 flex-shrink-0"
                title={t("ct.label.save")}
                aria-label={t("ct.label.save")}
              >
                <FiCheck className="w-4 h-4" />
              </button>
              {customLabel ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => persist(null)}
                  className="p-1.5 rounded-[5px] text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50 flex-shrink-0"
                  title={t("ct.label.remove")}
                  aria-label={t("ct.label.remove")}
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="p-1.5 rounded-[5px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-colors flex-shrink-0"
                  title={t("ct.label.cancel")}
                  aria-label={t("ct.label.cancel")}
                >
                  <FiX className="w-4 h-4" />
                </button>
              )}
            </form>
            <span className="block mt-1.5 text-[10px] leading-snug text-gray-400 dark:text-gray-500">
              {t("ct.label.hint")}
            </span>
          </div>,
          document.body
        )
      : null;

  if (mode === "chip") {
    // Trader-profile header: the real handle stays as the H1; the label is
    // an additional private chip (or a subtle "+ tag" affordance).
    return (
      <span ref={anchorRef} className="relative inline-flex items-center">
        {customLabel ? (
          <button
            type="button"
            onClick={startEditing}
            className="inline-flex items-center gap-1 max-w-[160px] px-2 py-0.5 rounded-[5px] text-[11px] font-semibold bg-gray-100 dark:bg-white/[0.06] text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-[#2A2A30] hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors"
            title={t("ct.label.edit")}
            aria-label={t("ct.label.edit")}
          >
            <FiTag className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{customLabel}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] text-[11px] font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
            title={t("ct.label.edit")}
            aria-label={t("ct.label.edit")}
          >
            <FiTag className="w-3 h-3" />
            {t("ct.label.add")}
          </button>
        )}
        {popover}
      </span>
    );
  }

  // "name" mode
  const display = traderDisplayName(username, address, customLabel);
  const realName = traderDisplayName(username, address);
  return (
    <span ref={anchorRef} className="relative flex items-center gap-1 min-w-0">
      {href ? (
        <Link href={href} title={realName} className={nameClassName}>
          {display}
        </Link>
      ) : (
        <span title={realName} className={nameClassName}>
          {display}
        </span>
      )}
      <button
        type="button"
        onClick={startEditing}
        className="p-1 rounded-[5px] text-gray-300 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-colors flex-shrink-0"
        title={t("ct.label.edit")}
        aria-label={t("ct.label.edit")}
      >
        <FiEdit2 className="w-3 h-3" />
      </button>
      {popover}
    </span>
  );
}
